import {
  FC,
  memo,
  ReactNode,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useState,
} from 'react'

import { Document, Page, pdfjs } from 'react-pdf'
import styled from 'styled-components'

import {
  Box,
  Button,
  Flex,
  Modal,
} from 'app/components/design-system-components'
import { AttachmentRounded } from 'app/components/design-system-components/icons/file'
import { PersonOutlineRounded } from 'app/components/design-system-components/icons/social'
import { fontWeight } from 'app/components/design-system-components/styles/variables'
import type { DataType } from 'app/components/display/smartdocs/explainer'
import ExplainerWithHeading from 'app/components/display/smartdocs/explainerWithHeading'
import { StyledEllipse } from 'app/components/property/PropertySummary'
import theme from 'app/match/applicationReportPDF/assets/theme'
import * as dateTimeHelpers from 'app/utils/datetime/helpers'
import { formatPhoneNumber } from 'app/utils/phonenumber/helpers'
import { trimAndAddEllipsesPastSpecifiedLength } from 'app/utils/strings/helpers'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface IDocumentViewerAttachments {
  url: string
  name: string
  typeOfDocument?: string
  description?: string
  guidID: string
  isAutoDeleted?: boolean
  fileAutoDeletedAt?: string
  redactionState?: string
}

interface IManualRedactionBoundingBox {
  left: number
  top: number
  width: number
  height: number
}

interface IManualRedactionArea {
  page: number
  boundingBox: IManualRedactionBoundingBox
}

interface IManualRedactionSavePayload {
  documentGuid: string
  areas: IManualRedactionArea[]
}

interface IDocumentViewerProps {
  toggleDocumentsViewer: () => void
  currentApplicantFullName?: string
  currentApplicantEmail?: string
  currentApplicantPhone?: string
  documentViewerAttachments: IDocumentViewerAttachments[]
  documentsViewerGUID: string
  smartDocsAnalysis?: DataType
  enableManualRedaction?: boolean
  hidePrintButton?: boolean
  onSaveManualRedactions?: (
    payload: IManualRedactionSavePayload,
  ) => Promise<void> | void
  onClickConfirmOKForAnalysis: (analysisGUID: string) => void
  onClickUndoForAnalysis: (
    analysisGUID: string,
    confirmationGUID: string,
  ) => void
}

interface IDraftManualRedaction {
  startX: number
  startY: number
  endX: number
  endY: number
}

interface IManualRedactionOverlayProps {
  isDrawingEnabled: boolean
  page: number
  savedAreas: IManualRedactionArea[]
  onAreaCreated: (area: IManualRedactionArea) => void
}

interface IRenderedPdfPageProps {
  isManualRedactionEnabled: boolean
  page: number
}

interface IManualRedactionPageProps {
  isDrawingEnabled: boolean
  isManualRedactionEnabled: boolean
  onAreaCreated: (area: IManualRedactionArea) => void
  page: number
  savedAreas: IManualRedactionArea[]
}

const bankStatementRedactionStateReady = 'ready'
const bankStatementRedactedUrlFragment = 'bank-redacted'

const StyledPdfPageContainer = styled(Box)`
  position: relative;
  display: inline-block;
  margin-top: ${theme.space[4]}px;
  line-height: 0;
`

const StyledDocument = styled(Box)`
  border: 1px solid ${theme.colors.gray400};
`

const StyledManualRedactionToolbar = styled(Flex)`
  gap: ${theme.space[2]}px;
  margin-top: ${theme.space[4]}px;
  padding: ${theme.space[3]}px;
  border: 1px solid ${theme.colors.gray300};
  border-radius: ${theme.radii[2]}px;
  background: ${theme.colors.white};
  flex-wrap: wrap;
`

const StyledManualRedactionCanvas = styled(Box)<{
  $isDrawingEnabled: boolean
}>`
  position: absolute;
  inset: 0;
  cursor: ${({ $isDrawingEnabled }) =>
    $isDrawingEnabled ? 'crosshair' : 'default'};
  pointer-events: ${({ $isDrawingEnabled }) =>
    $isDrawingEnabled ? 'auto' : 'none'};
  user-select: none;
  touch-action: none;
`

const StyledManualRedactionButtons = styled(Flex)`
  gap: ${theme.space[2]}px;
  align-items: center;
  flex-wrap: wrap;
`

const StyledManualRedactionBox = styled(Box)<{ $isDraft?: boolean }>`
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  border: ${({ $isDraft }) =>
    $isDraft ? '2px dashed rgba(255, 255, 255, 0.75)' : 'none'};
`

export const clampNormalizedCoordinate = (value: number) =>
  Math.max(0, Math.min(1, value))

export const buildManualRedactionArea = (
  page: number,
  draft: IDraftManualRedaction | null,
): IManualRedactionArea | null => {
  if (!draft) {
    return null
  }

  const left = clampNormalizedCoordinate(Math.min(draft.startX, draft.endX))
  const top = clampNormalizedCoordinate(Math.min(draft.startY, draft.endY))
  const width = clampNormalizedCoordinate(Math.abs(draft.endX - draft.startX))
  const height = clampNormalizedCoordinate(Math.abs(draft.endY - draft.startY))

  if (width < 0.01 || height < 0.01) {
    return null
  }

  return {
    page,
    boundingBox: { left, top, width, height },
  }
}

export const resolveNormalizedCoordinates = (
  event: Pick<ReactPointerEvent<HTMLDivElement>, 'clientX' | 'clientY'>,
  target: HTMLDivElement,
) => {
  const rect = target.getBoundingClientRect()
  if (!rect.width || !rect.height) {
    return null
  }

  return {
    x: clampNormalizedCoordinate((event.clientX - rect.left) / rect.width),
    y: clampNormalizedCoordinate((event.clientY - rect.top) / rect.height),
  }
}

const ManualRedactionOverlay: FC<IManualRedactionOverlayProps> = ({
  isDrawingEnabled,
  page,
  savedAreas,
  onAreaCreated,
}) => {
  const [draftRedaction, setDraftRedaction] =
    useState<IDraftManualRedaction | null>(null)

  useEffect(() => {
    if (!isDrawingEnabled) {
      setDraftRedaction(null)
    }
  }, [isDrawingEnabled])

  const releaseCapturedPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    }
  }

  const startDrawing = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.isPrimary === false || event.button !== 0) {
      return
    }

    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)

    const coordinates = resolveNormalizedCoordinates(event, event.currentTarget)
    if (!coordinates) {
      return
    }

    setDraftRedaction({
      startX: coordinates.x,
      startY: coordinates.y,
      endX: coordinates.x,
      endY: coordinates.y,
    })
  }

  const updateDrawing = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draftRedaction) {
      return
    }

    event.preventDefault()
    const coordinates = resolveNormalizedCoordinates(event, event.currentTarget)
    if (!coordinates) {
      return
    }

    setDraftRedaction({
      ...draftRedaction,
      endX: coordinates.x,
      endY: coordinates.y,
    })
  }

  const finishDrawing = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draftRedaction) {
      return
    }

    event.preventDefault()
    releaseCapturedPointer(event)
    const coordinates = resolveNormalizedCoordinates(event, event.currentTarget)
    const finalizedDraft = coordinates
      ? {
          ...draftRedaction,
          endX: coordinates.x,
          endY: coordinates.y,
        }
      : draftRedaction
    const finalizedArea = buildManualRedactionArea(page, finalizedDraft)

    if (finalizedArea) {
      onAreaCreated(finalizedArea)
    }

    setDraftRedaction(null)
  }

  const cancelDrawing = (event?: ReactPointerEvent<HTMLDivElement>) => {
    if (event) {
      releaseCapturedPointer(event)
    }
    setDraftRedaction(null)
  }

  const draftArea = buildManualRedactionArea(page, draftRedaction)

  return (
    <StyledManualRedactionCanvas
      $isDrawingEnabled={isDrawingEnabled}
      data-manual-redaction-overlay={`page-${page}`}
      onPointerDown={startDrawing}
      onPointerMove={updateDrawing}
      onPointerUp={finishDrawing}
      onPointerCancel={cancelDrawing}
      onPointerLeave={cancelDrawing}
    >
      {savedAreas.map((area) => (
        <StyledManualRedactionBox
          key={`${page}-${area.boundingBox.left}-${area.boundingBox.top}-${area.boundingBox.width}-${area.boundingBox.height}`}
          style={{
            left: `${area.boundingBox.left * 100}%`,
            top: `${area.boundingBox.top * 100}%`,
            width: `${area.boundingBox.width * 100}%`,
            height: `${area.boundingBox.height * 100}%`,
          }}
        />
      ))}
      {draftArea && (
        <StyledManualRedactionBox
          $isDraft
          data-manual-redaction-draft="true"
          style={{
            left: `${draftArea.boundingBox.left * 100}%`,
            top: `${draftArea.boundingBox.top * 100}%`,
            width: `${draftArea.boundingBox.width * 100}%`,
            height: `${draftArea.boundingBox.height * 100}%`,
          }}
        />
      )}
    </StyledManualRedactionCanvas>
  )
}

const RenderedPdfPage = memo(
  ({ isManualRedactionEnabled, page }: IRenderedPdfPageProps) => (
    <StyledDocument>
      <Page
        pageNumber={page}
        renderAnnotationLayer={!isManualRedactionEnabled}
        renderTextLayer={!isManualRedactionEnabled}
      />
    </StyledDocument>
  ),
)

const ManualRedactionPage: FC<IManualRedactionPageProps> = ({
  isDrawingEnabled,
  isManualRedactionEnabled,
  onAreaCreated,
  page,
  savedAreas,
}) => (
  <StyledPdfPageContainer>
    <RenderedPdfPage
      isManualRedactionEnabled={isManualRedactionEnabled}
      page={page}
    />
    {isManualRedactionEnabled && (
      <ManualRedactionOverlay
        isDrawingEnabled={isDrawingEnabled}
        page={page}
        savedAreas={savedAreas}
        onAreaCreated={onAreaCreated}
      />
    )}
  </StyledPdfPageContainer>
)

const DocumentsViewer: FC<IDocumentViewerProps> = ({
  currentApplicantFullName = '',
  currentApplicantEmail = '',
  currentApplicantPhone = '',
  toggleDocumentsViewer,
  documentViewerAttachments,
  documentsViewerGUID,
  smartDocsAnalysis,
  enableManualRedaction = false,
  hidePrintButton = false,
  onSaveManualRedactions,
  onClickConfirmOKForAnalysis,
  onClickUndoForAnalysis,
}) => {
  const totalAttachments = documentViewerAttachments?.length
  const [currentNumAttachment, setCurrentNumAttachment] = useState(0)
  const [
    smartDocsAnalysisForCurrentAttachment,
    setSmartDocsAnalysisForCurrentAttachment,
  ] = useState<DataType | undefined>()
  const [numPages, setNumPages] = useState<number | null>(null)
  const [isManualRedactionMode, setIsManualRedactionMode] = useState(false)
  const [isSavingManualRedactions, setIsSavingManualRedactions] =
    useState(false)
  const [manualRedactionsByDocument, setManualRedactionsByDocument] = useState<
    Record<string, IManualRedactionArea[]>
  >({})

  useEffect(() => {
    if (!documentsViewerGUID) {
      return
    }

    const selectedAttachmentIndex = documentViewerAttachments.findIndex(
      (documentViewerAttachment) =>
        documentViewerAttachment.guidID === documentsViewerGUID,
    )

    if (selectedAttachmentIndex >= 0) {
      setCurrentNumAttachment(selectedAttachmentIndex)
    }
  }, [documentViewerAttachments, documentsViewerGUID])

  const currentAttachment = documentViewerAttachments?.[currentNumAttachment]
  const currentAttachmentGuid = currentAttachment?.guidID || ''

  useEffect(() => {
    let formedAnalysisData: DataType | undefined
    const analysisForCurrentAttachment =
      smartDocsAnalysis?.analysis[currentAttachmentGuid]

    if (analysisForCurrentAttachment) {
      formedAnalysisData = {
        analysis: {
          [currentAttachmentGuid]: analysisForCurrentAttachment,
        },
      }
    }

    setSmartDocsAnalysisForCurrentAttachment(formedAnalysisData)
  }, [currentAttachmentGuid, smartDocsAnalysis])

  useEffect(() => {
    setIsManualRedactionMode(false)
    setNumPages(null)
  }, [currentAttachmentGuid])

  const attachmentNameParts = (currentAttachment?.name || '').split('.')
  const typeOfAttachment =
    attachmentNameParts[attachmentNameParts.length - 1] || ''
  const attachmentFileName =
    attachmentNameParts.slice(0, -1).join('.') || currentAttachment?.name || ''
  const attachmentFileNameToDisplay = trimAndAddEllipsesPastSpecifiedLength(
    attachmentFileName,
    30,
  )
  const currentManualRedactions =
    manualRedactionsByDocument[currentAttachmentGuid] || []
  const currentAttachmentHasRedactedPDF = Boolean(
    currentAttachment?.url?.includes(bankStatementRedactedUrlFragment) ||
      currentAttachment?.redactionState === bankStatementRedactionStateReady,
  )
  const isManualRedactionEnabledForCurrentAttachment = Boolean(
    enableManualRedaction &&
      currentAttachmentGuid &&
      currentAttachment?.url &&
      typeOfAttachment?.toLowerCase() === 'pdf' &&
      currentAttachmentHasRedactedPDF,
  )
  const shouldHidePrintButton = Boolean(
    hidePrintButton && isManualRedactionEnabledForCurrentAttachment,
  )

  const onDocumentLoadSuccess = ({
    numPages: nextNumPages,
  }: {
    numPages: number
  }) => {
    setNumPages(nextNumPages)
  }

  const addManualRedactionArea = (area: IManualRedactionArea) => {
    setManualRedactionsByDocument((previousRedactions) => ({
      ...previousRedactions,
      [currentAttachmentGuid]: [
        ...(previousRedactions[currentAttachmentGuid] || []),
        area,
      ],
    }))
  }

  const clearManualRedactions = () => {
    if (!currentAttachmentGuid) {
      return
    }

    setManualRedactionsByDocument((previousRedactions) => ({
      ...previousRedactions,
      [currentAttachmentGuid]: [],
    }))
  }

  const saveManualRedactions = async () => {
    if (
      !onSaveManualRedactions ||
      !currentAttachmentGuid ||
      currentManualRedactions.length === 0
    ) {
      return
    }

    setIsSavingManualRedactions(true)
    try {
      await onSaveManualRedactions({
        documentGuid: currentAttachmentGuid,
        areas: currentManualRedactions,
      })
      setManualRedactionsByDocument((previousRedactions) => ({
        ...previousRedactions,
        [currentAttachmentGuid]: [],
      }))
      setIsManualRedactionMode(false)
    } catch {
      // Error is handled by the caller (e.g. snugNotifier.error in container)
    } finally {
      setIsSavingManualRedactions(false)
    }
  }

  const renderManualRedactionToolbar =
    isManualRedactionEnabledForCurrentAttachment ? (
      <StyledManualRedactionToolbar
        alignItems="center"
        justifyContent="space-between"
      >
        <StyledManualRedactionButtons>
          <Button
            sizeVariant="extraSmall"
            variant="outlineSuccess"
            onClick={() => {
              setIsManualRedactionMode(!isManualRedactionMode)
            }}
          >
            {isManualRedactionMode ? 'Stop Drawing' : 'Draw Redactions'}
          </Button>
          <Button
            sizeVariant="extraSmall"
            variant="outlineSuccess"
            disabled={currentManualRedactions.length === 0}
            onClick={clearManualRedactions}
          >
            Clear
          </Button>
          <Button
            sizeVariant="extraSmall"
            variant="outlineSuccess"
            disabled={
              currentManualRedactions.length === 0 || isSavingManualRedactions
            }
            onClick={() => {
              saveManualRedactions()
            }}
          >
            {isSavingManualRedactions ? 'Saving...' : 'Save Redactions'}
          </Button>
        </StyledManualRedactionButtons>
        <Box
          color={theme.colors.gray600}
          data-manual-redaction-count={String(currentManualRedactions.length)}
        >
          {currentManualRedactions.length}{' '}
          {currentManualRedactions.length === 1
            ? 'redaction drawn'
            : 'redactions drawn'}
        </Box>
      </StyledManualRedactionToolbar>
    ) : null

  let attachmentRender: ReactNode = <Box></Box>
  if (currentAttachment?.isAutoDeleted) {
    attachmentRender = (
      <Box mt={10} textAlign="center">
        <Box color={theme.colors.primary300}>
          This attachment has been auto-deleted on{' '}
          {dateTimeHelpers.formatStdDateStamp(
            currentAttachment?.fileAutoDeletedAt,
          )}
          .
        </Box>
      </Box>
    )
  } else if (typeOfAttachment?.toLowerCase() === 'pdf') {
    attachmentRender = currentAttachment?.url ? (
      <Document
        file={currentAttachment?.url}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        {Array.from({ length: numPages || 0 }, (_, index) => {
          const page = index + 1
          const savedRedactionsForPage = currentManualRedactions.filter(
            (area) => area.page === page,
          )

          return (
            <ManualRedactionPage
              key={page}
              isDrawingEnabled={isManualRedactionMode}
              isManualRedactionEnabled={
                isManualRedactionEnabledForCurrentAttachment
              }
              onAreaCreated={addManualRedactionArea}
              page={page}
              savedAreas={savedRedactionsForPage}
            />
          )
        })}
      </Document>
    ) : null
  } else {
    attachmentRender = (
      <StyledDocument>
        <img
          src={currentAttachment?.url || ''}
          alt="attachment rendered"
          width="100%"
        />
      </StyledDocument>
    )
  }

  const applicantDetails = (
    <Flex alignItems="center">
      <PersonOutlineRounded color={theme.colors.gray500} />{' '}
      <Box color={theme.colors.gray800} ml={2} fontWeight={fontWeight[4]}>
        {currentApplicantFullName}
      </Box>
      <Box mr={2} ml={2} fontSize={theme.fontSizes.pRegular14}>
        <a href={`tel:${currentApplicantPhone}`}>
          {formatPhoneNumber(currentApplicantPhone)}
        </a>
      </Box>
      <StyledEllipse />
      <Box ml={2} fontSize={theme.fontSizes.pRegular14}>
        <a href={`mailTo:${currentApplicantPhone}`}>{currentApplicantEmail}</a>
      </Box>
    </Flex>
  )
  const showApplicantDetails = Boolean(
    currentApplicantFullName || currentApplicantEmail || currentApplicantPhone,
  )
  const modalBody = (
    <Box
      width="100%"
      data-document-viewer-modal="true"
      data-document-viewer-current-url={currentAttachment?.url || ''}
      data-document-viewer-current-name={currentAttachment?.name || ''}
    >
      <Flex justifyContent="space-between">
        <Box>
          {showApplicantDetails && <Box mb={5}>{applicantDetails}</Box>}
          <Flex alignItems="center">
            <AttachmentRounded color={theme.colors.gray800} />
            <Flex ml={2} alignItems="center">
              <Box
                color={theme.colors.skyBlue}
                onClick={() => window.open(currentAttachment?.url)}
                style={{ cursor: 'pointer' }}
              >
                {attachmentFileNameToDisplay}.{typeOfAttachment}
              </Box>
              {currentAttachment?.typeOfDocument && (
                <Box ml={2}>({currentAttachment?.typeOfDocument})</Box>
              )}
            </Flex>
          </Flex>
          {currentAttachment?.description && (
            <Box
              mt={2}
              ml={7}
              color={theme.colors.gray500}
              fontSize={theme.fontSizes.pSmall12}
            >
              {currentAttachment.description}
            </Box>
          )}
        </Box>
        {!shouldHidePrintButton && (
          <Box textAlign="right">
            <Button
              sizeVariant="extraSmall"
              variant="outlineSuccess"
              onClick={() => {
                window.open(currentAttachment?.url)
              }}
            >
              Print
            </Button>
          </Box>
        )}
      </Flex>
      {renderManualRedactionToolbar}
      <ExplainerWithHeading
        isCopilot={false}
        isRenterSide={false}
        data={smartDocsAnalysisForCurrentAttachment || undefined}
        onClickConfirmOKForAnalysis={onClickConfirmOKForAnalysis}
        onClickUndoForAnalysis={onClickUndoForAnalysis}
        withLearnMore
      />
      <Box mt={5}>{attachmentRender}</Box>
    </Box>
  )

  const nextAttachment = () => {
    if (currentNumAttachment < totalAttachments - 1) {
      setCurrentNumAttachment(currentNumAttachment + 1)
    }
  }

  const prevAttachment = () => {
    if (currentNumAttachment > 0) {
      setCurrentNumAttachment(currentNumAttachment - 1)
    }
  }

  const showPrimaryButton = currentNumAttachment < totalAttachments - 1
  const showSecondaryButton = currentNumAttachment > 0
  const useCustomModalFooter = isManualRedactionEnabledForCurrentAttachment

  return (
    <Box>
      <Modal
        modalHeading="Attachments"
        modalBody={modalBody}
        modalFooter={
          useCustomModalFooter ? undefined : (
            <Box mt={2}>
              {currentNumAttachment + 1} of {totalAttachments} attachments
            </Box>
          )
        }
        customModalFooter={useCustomModalFooter}
        primaryLabel={useCustomModalFooter ? undefined : 'Next'}
        primaryAction={useCustomModalFooter ? undefined : nextAttachment}
        secondaryLabel={useCustomModalFooter ? undefined : 'Previous'}
        secondaryAction={useCustomModalFooter ? undefined : prevAttachment}
        primaryButtonDisabled={!showPrimaryButton}
        secondaryButtonDisabled={!showSecondaryButton}
        toggleModal={toggleDocumentsViewer}
        takeIncreasedWidth
        contentClass="min-height-78vh"
      />
    </Box>
  )
}

export default DocumentsViewer
